/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { initAxios } from '../utils/axiosHelper';
import { parseErrorMsg } from '../utils/errorHelper';

// Which of the trial's one-free-run AI workflows the team has already used.
export interface AiWorkflowTrialUsage {
  export_used: boolean;
  import_used: boolean;
  design_used: boolean;
  beneficial_ownership_used: boolean;
}

// Shape of CreditsAPI.getCreditsSummary's `result` (paid and trial responses
// share the workflow flags; the credit fields differ by plan).
export interface CreditsSummary extends AiWorkflowTrialUsage {
  total_monthly_credits?: number;
  total_used_credits?: number;
  remaining_credits: number;
  db_size?: number;
  product_db_size?: number;
  total_workflow_credits?: number;
  total_used_workflow_credits?: number;
  remaining_workflow_credits?: number;
  unlimited_workflows: boolean;
}

export const CreditsAPI = {
  // Server-side proxy for the Stripe credit-pack catalog (Product name +
  // Price nickname "{credits} credits") -- replaces the old
  // REACT_APP_*_PRICE client env vars, so a fixed/added Stripe price is live
  // without a client rebuild. See server's creditPackCatalog.service.ts.
  getCreditPackCatalog: async (): Promise<any> =>
    (await initAxios())
      ?.get('/credits/packs')
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  addCredits: async (payload: {
    teamId: string;
    amount: number;
  }): Promise<any> =>
    (await initAxios())
      ?.post('/credits/add', JSON.stringify(payload))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  // Super-admin only -- the team's unlimited_workflows flag: when set, workflow
  // tool spends (import/export/design/beneficial ownership) are skipped
  // server-side; analyzer/tools/DB spends are unaffected.
  getUnlimitedWorkflows: async (teamId: string): Promise<any> =>
    (await initAxios())
      ?.get(`/credits/unlimited-workflows/${teamId}`)
      .then((resp) => ({ success: true, result: resp.data }))
      .catch((err) => ({ success: false, result: parseErrorMsg(err) })),
  setUnlimitedWorkflows: async (payload: {
    teamId: string;
    enabled: boolean;
  }): Promise<any> =>
    (await initAxios())
      ?.post('/credits/unlimited-workflows/set', JSON.stringify(payload))
      .then((resp) => ({ success: true, result: resp.data }))
      .catch((err) => ({ success: false, result: parseErrorMsg(err) })),
  // Super-admin only -- grants into Workflows' own pool
  // (team_credits.workflow_credits), separate from addCredits' Tools pool.
  addWorkflowCredits: async (payload: {
    teamId: string;
    amount: number;
  }): Promise<any> =>
    (await initAxios())
      ?.post('/credits/workflow-credits/add', JSON.stringify(payload))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  setDbSize: async (payload: {
    teamId: string;
    amount: number;
    field?: 'db_size' | 'product_db_size' | 'verification_credits';
  }): Promise<any> =>
    (await initAxios())
      ?.post('/credits/db-size/set', JSON.stringify(payload))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  addBackVerificationCredits: async (payload: {
    teamId: string;
    credits: number;
  }): Promise<any> =>
    (await initAxios())
      ?.post('/credits/verification/add-back', JSON.stringify(payload))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  convertProductDbSize: async (payload: {
    teamId: string;
    amount: number;
  }): Promise<any> =>
    (await initAxios())
      ?.post('/credits/db-size/convert-product', JSON.stringify(payload))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  spendCredits: async (payload: {
    teamId: string;
    source:
      | 'import_workflow'
      | 'export_workflow'
      | 'beneficial_ownership'
      | 'design_assistant';
    referenceId: string | null;
    // 1-based workflow step this spend settles. Omit to bill every step the
    // server has not yet billed for this referenceId.
    step?: number;
    // Unique id of this workflow run; the server bills each (runId, step) once and
    // records both in the ledger.
    runId?: string | null;
    // Sourced from GlobalProvider's cur_user so the log reflects the real
    // acting user even in edge cases where the JWT's own name/email claims
    // are incomplete. The server prefers its own JWT-derived actor and only
    // falls back to these when that's missing a field.
    userId?: string | null;
    userName?: string | null;
    userEmail?: string | null;
  }): Promise<any> =>
    (await initAxios())
      ?.post('/credits/spend', JSON.stringify(payload))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  getVerificationCreditsSummary: async (team_id: string): Promise<any> =>
    (await initAxios())
      ?.get(`/credits/verification/summary/${team_id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  // Super-admin console: same figures for a team the admin isn't a member of.
  getAdminVerificationCreditsSummary: async (team_id: string): Promise<any> =>
    (await initAxios())
      ?.get(`/credits/verification/admin-summary/${team_id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  getCreditsSummary: async (
    team_id: string,
  ): Promise<{ success: boolean; result: CreditsSummary | any }> =>
    (await initAxios())
      ?.get(`/credits/summary/${team_id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  seedTrialCredits: async (payload: { teamId: string }): Promise<any> =>
    (await initAxios())
      ?.get('/credits/trial/seed', {
        params: {
          teamId: payload.teamId,
        },
      })
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  getTrialCreditsSummary: async (team_id: string): Promise<any> =>
    (await initAxios())
      ?.get(`/credits/trial/summary/${team_id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  spendTrialCredits: async (payload: {
    teamId: string;
    moduleKey: string;
    source:
      | 'import_workflow'
      | 'export_workflow'
      | 'beneficial_ownership'
      | 'design_assistant';
    referenceId: string | null;
    userId?: string | null;
    userName?: string | null;
    userEmail?: string | null;
  }): Promise<any> =>
    (await initAxios())
      ?.post('/credits/trial/spend', JSON.stringify(payload))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  addTrialCredits: async (payload: {
    teamId: string;
    amount: number;
  }): Promise<any> =>
    (await initAxios())
      ?.post('/credits/trial/add', JSON.stringify(payload))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  // Super-admin only (realm:bite-admin) -- powers TeamSelectView's Usage tab,
  // where the caller is viewing a team they are not necessarily a member of.
  getAdminUsageBreakdown: async (team_id: string): Promise<any> =>
    (await initAxios())
      ?.get(`/credits/usage/${team_id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  getAdminTransactions: async (team_id: string, limit = 50): Promise<any> =>
    (await initAxios())
      ?.get(`/credits/transactions/${team_id}`, { params: { limit: limit } })
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  // Team-scoped counterparts of the two calls above, for an ordinary member
  // viewing their OWN team's usage (the Subscriptions page's Credits/Usage
  // section) -- see credits/router.ts for why these are separate paths
  // ('my-usage'/'my-transactions') rather than reusing '/usage'/'/transactions'.
  // Super-admin (realm:bite-admin) copies of getTransactions/getUsageSeries for
  // viewing a team the caller is not a member of (TeamSelectView's Usage tab).
  getAdminDatabaseUsage: async (team_id: string): Promise<any> =>
    (await initAxios())
      ?.get(`/credits/database-usage/${team_id}`)
      .then((resp) => ({ success: true, result: resp.data }))
      .catch((err) => ({ success: false, result: parseErrorMsg(err) })),
  getAdminUsageFeed: async (
    team_id: string,
    days = 30,
    limit = 500,
  ): Promise<any> =>
    (await initAxios())
      ?.get(`/credits/usage-feed/${team_id}`, {
        params: { days: days, limit: limit },
      })
      .then((resp) => ({ success: true, result: resp.data }))
      .catch((err) => ({ success: false, result: parseErrorMsg(err) })),
  getAdminUsageSeries: async (
    team_id: string,
    days: number,
    bucket: 'hour' | 'day',
    tz: string,
  ): Promise<any> =>
    (await initAxios())
      ?.get(`/credits/usage-series/${team_id}`, {
        params: { days: days, bucket: bucket, tz: tz },
      })
      .then((resp) => ({ success: true, result: resp.data }))
      .catch((err) => ({ success: false, result: parseErrorMsg(err) })),
  getUsageBreakdown: async (team_id: string): Promise<any> =>
    (await initAxios())
      ?.get(`/credits/my-usage/${team_id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  getTransactions: async (
    team_id: string,
    days = 30,
    limit = 500,
  ): Promise<any> =>
    (await initAxios())
      ?.get(`/credits/my-transactions/${team_id}`, {
        params: { days: days, limit: limit },
      })
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  // Full-window hourly/daily totals aggregated server-side (getTransactions is
  // row-capped, so it can't be summed for high-volume teams).
  getUsageSeries: async (
    team_id: string,
    days: number,
    bucket: 'hour' | 'day',
    tz: string,
  ): Promise<any> =>
    (await initAxios())
      ?.get(`/credits/my-usage-series/${team_id}`, {
        params: { days: days, bucket: bucket, tz: tz },
      })
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  // Super-admin only -- per-database-type (entity/product/export) daily
  // added-record counts for TeamSelectView's Usage tab.
  getAdminDatabaseLog: async (team_id: string): Promise<any> =>
    (await initAxios())
      ?.get(`/credits/database-log/${team_id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  setTrialDbSize: async (payload: {
    teamId: string;
    amount: number;
    field?: 'db_size' | 'product_db_size';
  }): Promise<any> =>
    (await initAxios())
      ?.post('/credits/trial/db-size/set', JSON.stringify(payload))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
};

export default CreditsAPI;
